"use client"

import { useActionState } from "react"
import { sendContactEmail } from "@/app/actions/send-contact-email"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Loader2, CheckCircle } from "lucide-react"

export type ContactFormConfig = {
    locale: "de" | "ru"
    /** Landet im Betreff der Mail, z. B. "RU Landingpage". */
    source?: string
    /** Auswahl-Optionen. Leer → kein Select, `defaultService` wird versteckt mitgesendet. */
    services: readonly string[]
    defaultService?: string
    showCompany?: boolean
    showPhone?: boolean
    texts: {
        title: string
        description: string
        nameLabel: string
        namePlaceholder: string
        companyLabel: string
        companyPlaceholder: string
        phoneLabel: string
        phonePlaceholder: string
        emailLabel: string
        emailPlaceholder: string
        serviceLabel: string
        servicePlaceholder: string
        messageLabel: string
        messagePlaceholder: string
        submit: string
        sending: string
        successTitle: string
        successText: string
        successAgain: string
    }
}

/** Deutsche Standardkonfiguration – entspricht dem bisherigen Formular auf der Startseite. */
export const contactFormConfigDe: ContactFormConfig = {
    locale: "de",
    services: [
        "Managed Services",
        "Server Hosting & Verwaltung",
        "Container-Lösungen (Docker/Kubernetes)",
        "Automatisierung",
        "Full Stack Programmierung",
        "Webprogrammierung",
        "Microsoft 365 & Lizenzen",
        "Beratung & Support",
        "Sonstiges",
    ],
    texts: {
        title: "Kontaktformular",
        description: "Senden Sie mir eine Nachricht mit Ihrem Anliegen.",
        nameLabel: "Name / Unternehmen",
        namePlaceholder: "Max Mustermann GmbH",
        companyLabel: "Unternehmen",
        companyPlaceholder: "Mustermann GmbH",
        phoneLabel: "Telefon (optional)",
        phonePlaceholder: "+49 …",
        emailLabel: "E-Mail-Adresse",
        emailPlaceholder: "kontakt@beispiel.de",
        serviceLabel: "Gewünschte Dienstleistung",
        servicePlaceholder: "Bitte wählen...",
        messageLabel: "Ihre Nachricht",
        messagePlaceholder: "Beschreiben Sie Ihr Projekt oder Anliegen...",
        submit: "Nachricht senden",
        sending: "Wird gesendet...",
        successTitle: "Anfrage gesendet!",
        successText: "Vielen Dank für Ihre Nachricht. Ich werde mich schnellstmöglich bei Ihnen melden.",
        successAgain: "Neue Anfrage senden",
    },
}

const initialState = {
    errors: {},
}

const selectClassName =
    "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"

export function ContactForm({ config = contactFormConfigDe }: { config?: ContactFormConfig } = {}) {
    const [state, formAction, isPending] = useActionState(sendContactEmail, initialState)
    const { texts } = config
    const hasServiceSelect = config.services.length > 0

    if (state.success) {
        return (
            <Card className="w-full h-full flex flex-col items-center justify-center p-6 text-center animate-in fade-in-50">
                <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
                <CardTitle className="text-2xl mb-2">{texts.successTitle}</CardTitle>
                <CardDescription className="text-lg">
                    {texts.successText}
                </CardDescription>
                <Button
                    variant="outline"
                    className="mt-8"
                    onClick={() => window.location.reload()}
                >
                    {texts.successAgain}
                </Button>
            </Card>
        )
    }

    return (
        <Card className="w-full">
            <CardHeader>
                <CardTitle>{texts.title}</CardTitle>
                <CardDescription>
                    {texts.description}
                </CardDescription>
            </CardHeader>
            <CardContent>
                <form action={formAction} className="space-y-4">
                    {/* Honeypot - invisible to users, bots fill it in */}
                    <div className="absolute -left-[9999px]" aria-hidden="true" tabIndex={-1}>
                        <label htmlFor="website">Website</label>
                        <input type="text" id="website" name="website" tabIndex={-1} autoComplete="off" />
                    </div>
                    <input type="hidden" name="formLoadedAt" value={Date.now().toString()} />
                    {config.locale !== "de" && <input type="hidden" name="locale" value={config.locale} />}
                    {config.source && <input type="hidden" name="source" value={config.source} />}
                    {!hasServiceSelect && (
                        <input type="hidden" name="service" value={config.defaultService ?? "Anfrage"} />
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="name">{texts.nameLabel}</Label>
                        <Input
                            id="name"
                            name="name"
                            placeholder={texts.namePlaceholder}
                            required
                        />
                        {state.errors?.name && (
                            <p className="text-sm text-red-500">{state.errors.name.join(", ")}</p>
                        )}
                    </div>

                    {config.showCompany && (
                        <div className="space-y-2">
                            <Label htmlFor="company">{texts.companyLabel}</Label>
                            <Input
                                id="company"
                                name="company"
                                placeholder={texts.companyPlaceholder}
                                autoComplete="organization"
                            />
                            {state.errors?.company && (
                                <p className="text-sm text-red-500">{state.errors.company.join(", ")}</p>
                            )}
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="email">{texts.emailLabel}</Label>
                        <Input
                            id="email"
                            name="email"
                            type="email"
                            placeholder={texts.emailPlaceholder}
                            required
                        />
                        {state.errors?.email && (
                            <p className="text-sm text-red-500">{state.errors.email.join(", ")}</p>
                        )}
                    </div>

                    {config.showPhone && (
                        <div className="space-y-2">
                            <Label htmlFor="phone">{texts.phoneLabel}</Label>
                            <Input
                                id="phone"
                                name="phone"
                                type="tel"
                                placeholder={texts.phonePlaceholder}
                                autoComplete="tel"
                            />
                            {state.errors?.phone && (
                                <p className="text-sm text-red-500">{state.errors.phone.join(", ")}</p>
                            )}
                        </div>
                    )}

                    {hasServiceSelect && (
                        <div className="space-y-2">
                            <Label htmlFor="service">{texts.serviceLabel}</Label>
                            <select
                                id="service"
                                name="service"
                                className={selectClassName}
                                defaultValue=""
                                required
                            >
                                <option value="" disabled>{texts.servicePlaceholder}</option>
                                {config.services.map((service) => (
                                    <option key={service} value={service}>
                                        {service}
                                    </option>
                                ))}
                            </select>
                            {state.errors?.service && (
                                <p className="text-sm text-red-500">{state.errors.service.join(", ")}</p>
                            )}
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="message">{texts.messageLabel}</Label>
                        <Textarea
                            id="message"
                            name="message"
                            placeholder={texts.messagePlaceholder}
                            className="min-h-[120px]"
                            required
                        />
                        {state.errors?.message && (
                            <p className="text-sm text-red-500">{state.errors.message.join(", ")}</p>
                        )}
                    </div>

                    {state.error && (
                        <div className="p-3 rounded-md bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm">
                            {state.error}
                        </div>
                    )}

                    <Button type="submit" className="w-full" disabled={isPending}>
                        {isPending ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                {texts.sending}
                            </>
                        ) : (
                            texts.submit
                        )}
                    </Button>
                </form>
            </CardContent>
        </Card>
    )
}
